import { ReactNode, useState } from 'react';

import { LocateFixedIcon, SparklesIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Progress } from '@/components/ui/progress';
import { selectAccountSlug } from '@/redux/session/selectors';

import { ExpandToggle, NewTabStepLink, PlainRow } from './DetailsRow';
import { selectBrainId } from '../../../redux/selectors';
import { ConversationTurn, Intent } from '../../../types/conversation';

const toPercent = (confidence: number) => Math.round(confidence * 100);

/** The planner intent wins over the top NLU intent; an `offtopics`-only planner matched nothing. */
export const getIntentInfo = (turn: ConversationTurn) => {
  const candidates: Intent[] = turn.request?.intents ?? [];
  const plannerIntents = turn.metadata.plannerIntents ?? [];
  const isOfftopic = plannerIntents.length === 1 && plannerIntents[0] === 'offtopics';
  const plannerIntent = isOfftopic ? undefined : plannerIntents[0];
  const selectedName = plannerIntent ?? turn.metadata.intent?.intent;
  const showNoIntent = isOfftopic || !selectedName;
  const selectedConfidence = showNoIntent
    ? undefined
    : (candidates.find((c) => c.intent === selectedName)?.confidence ??
      turn.metadata.intent?.confidence);
  const hasInfo = !showNoIntent || candidates.length > 0;
  return { showNoIntent, selectedName, selectedConfidence, considered: candidates, hasInfo };
};

const IntentLink = ({ intent, children }: { intent: string; children: ReactNode }) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const brainId = useSelector(selectBrainId);

  return (
    <NewTabStepLink
      to={slug && brainId ? `/${slug}/ai-agents/${brainId}/workflows/intents/${intent}` : null}
      tooltip={t('try_it.details.go_to_intent')}
    >
      {children}
    </NewTabStepLink>
  );
};

export const IntentConfidenceBars = ({ intents }: { intents: Intent[] }) => {
  const ranked = [...intents].sort((a, b) => b.confidence - a.confidence);

  if (ranked.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col items-start gap-3 self-stretch pl-1">
      {ranked.map((candidate) => (
        <div
          key={candidate.intent}
          className="flex items-center justify-between gap-3 self-stretch p-0"
        >
          <LocateFixedIcon className="size-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate">
            <IntentLink intent={candidate.intent}>
              <span className="truncate">{candidate.intent}</span>
            </IntentLink>
          </span>
          <Progress
            className="h-1 w-20 shrink-0 bg-input"
            indicatorClassName="bg-muted-foreground"
            value={toPercent(candidate.confidence)}
            aria-label={candidate.intent}
          />
          <span className="w-8 shrink-0 text-right text-xs leading-4 text-muted-foreground">
            {toPercent(candidate.confidence)}%
          </span>
        </div>
      ))}
    </div>
  );
};

export type IntentInfo = ReturnType<typeof getIntentInfo>;

const IntentLine = ({ info }: { info: IntentInfo }) => {
  const { t } = useTranslation();
  const { showNoIntent, selectedName, selectedConfidence } = info;

  if (showNoIntent || !selectedName) {
    return <PlainRow>{t('try_it.no_intent_ai_agent')}</PlainRow>;
  }

  return (
    <PlainRow>
      {t('try_it.details.intent')}{' '}
      <SparklesIcon className="mr-1 inline size-4 align-text-bottom text-ai" />
      <IntentLink intent={selectedName}>{selectedName}</IntentLink>
      {typeof selectedConfidence === 'number' && (
        <> {t('try_it.selected_ai_agent_confidence', { 0: toPercent(selectedConfidence) })}</>
      )}
    </PlainRow>
  );
};

/** `amend` is injected rather than imported — `AmendRows` already depends on this module. */
export const IntentDetails = ({ info, amend }: { info: IntentInfo; amend?: ReactNode }) => {
  const { t } = useTranslation();
  const { considered } = info;
  const [expanded, setExpanded] = useState(false);
  const toggle = () => setExpanded((prev) => !prev);

  return (
    <div className="flex flex-1 flex-col items-start gap-4 pb-2">
      <div className="flex items-start justify-between gap-3 self-stretch p-0">
        <span className="min-w-0 flex-1">
          <IntentLine info={info} />
        </span>
        {considered.length > 0 && (
          <ExpandToggle expanded={expanded} onToggle={toggle} label={t('try_it.considered')} />
        )}
      </div>

      {expanded && <IntentConfidenceBars intents={considered} />}
      {amend}
    </div>
  );
};
